<template>
  <div v-if="isAdmin" class="intel-chat-container">
    <button class="intel-fab" @click="togglePanel" aria-label="فتح مساعد الذكاء">
      <i class="pi pi-comments"></i>
    </button>

    <transition name="slide-up">
      <div
        v-if="isOpen"
        class="intel-panel"
        @dragenter.prevent="handleDragEnter"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
      >
        <div v-if="isDragging" class="intel-drop-overlay">
          <i class="pi pi-images" />
          <p>أسقط الصور هنا</p>
        </div>
<header class="intel-header">
  <div>
    <h4>مساعد الذكاء للموارد البشرية</h4>
    <p class="subtitle">ألصق روابط أو نصوص عن الموظفين ليتولى المساعد توزيعها.</p>
  </div>
  <div class="intel-header-actions">
    <Button
      label="محادثة جديدة"
      icon="pi pi-refresh"
      text
      severity="secondary"
      :disabled="loading"
      @click="resetConversation"
    />
    <Button icon="pi pi-times" text rounded severity="secondary" @click="togglePanel" />
  </div>
</header>

        <div class="intel-body">
          <section class="intel-messages" ref="messagesContainer">
            <div
              v-for="msg in messages"
              :key="msg.id"
              class="message"
              :class="msg.role"
            >
              <p v-html="msg.text"></p>
            </div>
          </section>

          <section v-if="employeeChoices.length" class="intel-choices">
            <h5>اختر الموظف الصحيح</h5>
            <div
              v-for="choice in employeeChoices"
              :key="choice.choice_id"
              class="choice-block"
            >
              <p class="choice-label">
                {{ choice.label }}<br />
                <small class="text-muted">نتائج البحث عن "{{ choice.query }}"</small>
              </p>
              <div class="choice-options">
                <label
                  v-for="option in choice.options"
                  :key="`${choice.choice_id}-${option.employee_id}`"
                  class="choice-option"
                >
                  <RadioButton
                    :inputId="`${choice.choice_id}-${option.employee_id}`"
                    :value="option.employee_id"
                    v-model="selectedEmployeeChoices[choice.choice_id]"
                  />
                  <div class="choice-option-body">
                    <RouterLink
                      :to="`/employee/${option.employee_id}`"
                      class="choice-link"
                      target="_blank"
                    >
                      {{ option.label }}
                    </RouterLink>
                    <small>{{ option.line }}</small>
                  </div>
                </label>
                <p v-if="!choice.options.length" class="choice-empty text-muted">
                  لا يوجد موظفون مطابقون للاسم الحالي.
                </p>
                <label
                  v-if="choice.allow_create"
                  :key="`${choice.choice_id}-new`"
                  class="choice-option create-option"
                >
                  <RadioButton
                    :inputId="`${choice.choice_id}-new`"
                    value="new"
                    v-model="selectedEmployeeChoices[choice.choice_id]"
                  />
                  <div class="choice-option-body">
                    <span class="choice-link">إنشاء سجل موظف جديد</span>
                    <small>
                      سيتم إنشاء سجل جديد باسم
                      {{ [choice.seed_first_name, choice.seed_last_name].filter(Boolean).join(' ') || choice.query }}
                    </small>
                  </div>
                </label>
              </div>
            </div>
            <small class="choice-hint">الرجاء اختيار الموظف الصحيح لكل حالة قبل تأكيد الخطة.</small>
          </section>

          <section v-if="pendingPlan" class="intel-plan">
            <div class="plan-summary">
              <i class="pi pi-bolt"></i>
              <div>
                <p class="mb-1">{{ pendingPlan.summary }}</p>
                <small>راجع الخطوات المقترحة واختر ما تود تنفيذه</small>
              </div>
            </div>
            <div
              v-for="action in pendingPlan.actions"
              :key="action.action_id"
              class="plan-action"
            >
              <Checkbox
                v-model="selectedActions"
                :value="action.action_id"
                :binary="false"
                :disabled="loading || action.confirmation_required === false"
                :inputId="`action-${action.action_id}`"
              />
              <div class="action-details">
                <div class="action-headline">
                  <span class="action-title">{{ action.title }}</span>
                  <span class="action-chip">{{ action.action_type }}</span>
                </div>
                <p class="action-preview">{{ action.preview || action.description }}</p>
                <small v-if="action.confirmation_required === false" class="auto-pill">
                  سيتم تنفيذها تلقائياً
                </small>
              </div>
            </div>
            <small v-if="planError" class="p-error">{{ planError }}</small>
            <div class="flex gap-2 mt-3">
              <Button
                label="تأكيد"
                icon="pi pi-check"
                :disabled="loading || hasOutstandingChoices"
                @click="confirmPlan"
              />
              <Button
                label="إلغاء الخطة"
                icon="pi pi-times"
                severity="secondary"
                outlined
                :disabled="loading"
                @click="resetPlan"
              />
            </div>
          </section>
        </div>

        <footer class="intel-input">
          <Textarea
            v-model="draft"
            rows="3"
            autoResize
            placeholder="ألصق مقالات، روابط أو وصف باللغتين العربية أو الإنجليزية..."
            @keydown.enter.exact.prevent="handleEnter"
            :disabled="loading"
          />
          <div class="intel-upload-bar">
            <input
              ref="fileInput"
              data-testid="intel-file-input"
              class="intel-file-input"
              type="file"
              accept="image/*"
              multiple
              @change="handleAttachmentChange"
            />
            <Button
              label="إضافة صور"
              icon="pi pi-paperclip"
              size="small"
              text
              :disabled="loading"
              @click="triggerAttachmentPicker"
            />
            <small v-if="attachments.length" class="attachment-count">
              {{ attachments.length }} ملف/ملفات
            </small>
          </div>
          <ul v-if="attachments.length" class="attachment-preview">
            <li v-for="item in attachments" :key="item.id">
              <span>{{ item.filename }} · {{ formatBytes(item.size) }}</span>
              <button type="button" @click="removeAttachment(item.id)" aria-label="إزالة">
                <i class="pi pi-times" />
              </button>
            </li>
          </ul>
          <Button
            label="إرسال"
            icon="pi pi-send"
            :loading="loading"
            :disabled="!canSend"
            @click="sendMessage"
          />
        </footer>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Textarea from 'primevue/textarea'
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import { useToast } from 'primevue/usetoast'

import { useAuthStore } from '@/store/auth'
import {
  fetchIntelHistory,
  resetIntelHistory,
  sendIntelMessage,
  type IntelPlan,
  type EmployeeChoicePrompt,
  type EmployeeChoiceSelection,
  type IntelHistoryEntry,
  type IntelAttachment,
  type AdminIntelUIDirective,
} from '@/services/adminIntel'

type Message = {
  id: string
  role: 'user' | 'bot'
  text: string
}

type AttachmentDraft = {
  id: string
  filename: string
  contentType: string
  size: number
  data: string
}

type PayloadOptions = {
  message?: string
  planToken?: string | null
  approvedActions?: string[]
  rejectedActions?: string[]
  attachments?: IntelAttachment[]
}

const auth = useAuthStore()
const toast = useToast()
const router = useRouter()

const SESSION_STORAGE_KEY = 'organza-admin-intel-session'
const WELCOME_TEXT =
  'أنا مساعد إدخال المعلومات. ألصق النصوص أو الروابط وسأتولى توزيعها على الموظفين المناسبين.'
const hasWindow = typeof window !== 'undefined'
const MAX_UPLOAD_BYTES = 6 * 1024 * 1024

const isOpen = ref(false)
const sessionId = ref<string | null>(null)
const messages = ref<Message[]>([getWelcomeMessage()])
const draft = ref('')
const loading = ref(false)
const pendingPlan = ref<IntelPlan | null>(null)
const planToken = ref<string | null>(null)
const selectedActions = ref<string[]>([])
const planError = ref<string | null>(null)
const messagesContainer = ref<HTMLElement | null>(null)
const employeeChoices = ref<EmployeeChoicePrompt[]>([])
const selectedEmployeeChoices = ref<Record<string, number | 'new' | null>>({})
const attachments = ref<AttachmentDraft[]>([])
const isDragging = ref(false)
const dragDepth = ref(0)
const fileInput = ref<HTMLInputElement | null>(null)

function getWelcomeMessage(): Message {
  return {
    id: `welcome-${Date.now()}`,
    role: 'bot',
    text: WELCOME_TEXT,
  }
}

const isAdmin = computed(() => auth.isAdminRole)
const canSend = computed(() => (!loading.value && (!!draft.value.trim() || attachments.value.length > 0)))
const hasOutstandingChoices = computed(() =>
  employeeChoices.value.some((choice) => !selectedEmployeeChoices.value[choice.choice_id])
)

function togglePanel() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(scrollToBottom)
  } else {
    resetPlan()
  }
}

function scrollToBottom() {
  const el = messagesContainer.value
  if (el) el.scrollTop = el.scrollHeight
}

watch(
  messages,
  () => nextTick(scrollToBottom),
  { deep: true }
)

function pushMessage(role: Message['role'], text: string) {
  messages.value.push({
    id: `${role}-${Date.now()}-${Math.random()}`,
    role,
    text,
  })
}

function assignSession(id: string | null, persist = true) {
  sessionId.value = id
  if (!persist || !hasWindow) return
  if (id) {
    window.localStorage.setItem(SESSION_STORAGE_KEY, id)
  } else {
    window.localStorage.removeItem(SESSION_STORAGE_KEY)
  }
}

function applyHistory(entries?: IntelHistoryEntry[]) {
  if (!entries || !entries.length) {
    messages.value = [getWelcomeMessage()]
    return
  }
  messages.value = entries.map((entry, idx) => ({
    id: `hist-${idx}-${entry.role}`,
    role: entry.role === 'user' ? 'user' : 'bot',
    text: entry.content,
  }))
}

async function restoreHistoryFromServer(id: string) {
  try {
    const data = await fetchIntelHistory(id)
    applyHistory(data.history)
  } catch (err) {
    console.error('Failed to restore admin intel history', err)
    assignSession(null)
    applyHistory()
  }
}

onMounted(() => {
  if (!hasWindow) return
  const storedId = window.localStorage.getItem(SESSION_STORAGE_KEY)
  if (storedId) {
    assignSession(storedId, false)
    restoreHistoryFromServer(storedId)
  }
})

function buildEmployeeChoicePayload(): EmployeeChoiceSelection[] | undefined {
  const entries: EmployeeChoiceSelection[] = []
  Object.entries(selectedEmployeeChoices.value).forEach(([choiceId, value]) => {
    if (value === 'new') {
      entries.push({ choice_id: choiceId, create_new: true })
    } else if (typeof value === 'number') {
      entries.push({ choice_id: choiceId, employee_id: value })
    }
  })
  return entries.length ? entries : undefined
}

function setEmployeeChoices(choices: EmployeeChoicePrompt[] | undefined) {
  employeeChoices.value = choices || []
  const map: Record<string, number | 'new' | null> = {}
  employeeChoices.value.forEach((choice) => {
    map[choice.choice_id] = selectedEmployeeChoices.value[choice.choice_id] ?? null
  })
  selectedEmployeeChoices.value = map
}

function clearEmployeeChoices() {
  employeeChoices.value = []
  selectedEmployeeChoices.value = {}
}

async function sendMessage() {
  if (!canSend.value) return
  const content = draft.value.trim()
  draft.value = ''
  const displayText = content || (attachments.value.length ? 'تم إرسال مرفقات.' : '')
  pushMessage('user', displayText)
  const outgoingAttachments = attachments.value.map((item) => ({
    filename: item.filename,
    content_type: item.contentType,
    data: item.data,
  }))
  const ok = await processPayload({ message: content, attachments: outgoingAttachments })
  if (ok) {
    resetAttachments()
  }
}

async function processPayload(options: PayloadOptions): Promise<boolean> {
  if (!options.message && !options.planToken) return false
  loading.value = true
  planError.value = null
  try {
    const response = await sendIntelMessage({
      message: options.message,
      session_id: sessionId.value || undefined,
      plan_token: options.planToken || undefined,
      approved_action_ids: options.approvedActions || undefined,
      rejected_action_ids: options.rejectedActions || undefined,
      employee_choices: buildEmployeeChoicePayload(),
      attachments: options.attachments,
    })
    assignSession(response.session_id)
    applyHistory(response.history)
    setEmployeeChoices(response.employee_choices)
    runUIDirectives(response.ui_directives)

    if (response.requires_confirmation && response.plan?.actions.length) {
      pendingPlan.value = response.plan
      planToken.value = response.plan_token || null
      const confirmable = response.plan.actions
        .filter((action) => action.confirmation_required !== false)
        .map((action) => action.action_id)
      selectedActions.value = confirmable.length ? confirmable : response.plan.actions.map((a) => a.action_id)
      return true
    }

    resetPlan()
    toast.add({ severity: 'success', summary: 'تم التنفيذ', life: 2000 })
    return true
  } catch (err: any) {
    console.error(err)
    toast.add({
      severity: 'error',
      summary: 'خطأ',
      detail: err?.response?.data?.detail || 'تعذر تنفيذ الطلب',
      life: 4000,
    })
    return false
  } finally {
    loading.value = false
  }
}

function handleEnter() {
  if (!canSend.value) return
  sendMessage()
}

async function confirmPlan() {
  if (!pendingPlan.value || !planToken.value) return
  const autoActions = pendingPlan.value.actions
    .filter((action) => action.confirmation_required === false)
    .map((action) => action.action_id)
  const approved = Array.from(new Set([...selectedActions.value, ...autoActions]))
  if (!approved.length) {
    planError.value = 'الرجاء اختيار خطوة واحدة على الأقل'
    return
  }
  const rejected = pendingPlan.value.actions
    .map((action) => action.action_id)
    .filter((id) => !approved.includes(id))

  await processPayload({
    planToken: planToken.value,
    approvedActions: approved,
    rejectedActions: rejected,
  })
}

async function resetConversation() {
  try {
    if (sessionId.value) {
      await resetIntelHistory(sessionId.value)
    }
    assignSession(null)
  } catch (err: any) {
    console.error(err)
    toast.add({
      severity: 'error',
      summary: 'تعذر تصفير المحادثة',
      detail: err?.response?.data?.detail || 'حدث خلل أثناء إعادة التعيين',
      life: 4000,
    })
    return
  }
  draft.value = ''
  resetPlan()
  applyHistory()
  resetAttachments()
  toast.add({ severity: 'success', summary: 'تمت إعادة المحادثة', life: 2000 })
}

function resetPlan() {
  pendingPlan.value = null
  planToken.value = null
  selectedActions.value = []
  planError.value = null
  clearEmployeeChoices()
}

function triggerAttachmentPicker() {
  fileInput.value?.click()
}

async function handleAttachmentChange(event: Event) {
  const input = event.target as HTMLInputElement | null
  const files = input?.files ? Array.from(input.files) : []
  await addFiles(files)
  if (input) input.value = ''
}

async function addFiles(files: File[]) {
  if (!files.length) return
  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      toast.add({ severity: 'warn', summary: 'نوع غير مدعوم', detail: `لا يمكن رفع ${file.name}`, life: 4000 })
      continue
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.add({
        severity: 'warn',
        summary: 'ملف كبير',
        detail: `${file.name} يتجاوز الحد المسموح (6MB)`,
        life: 4000,
      })
      continue
    }
    const data = await fileToBase64(file)
    attachments.value.push({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      filename: file.name,
      contentType: file.type,
      size: file.size,
      data,
    })
  }
}

function removeAttachment(id: string) {
  attachments.value = attachments.value.filter((item) => item.id !== id)
}

function resetAttachments() {
  attachments.value = []
  dragDepth.value = 0
  isDragging.value = false
  if (fileInput.value) fileInput.value.value = ''
}

async function fileToBase64(file: File) {
  const buffer = await file.arrayBuffer()
  return arrayBufferToBase64(buffer)
}

function hasFilePayload(event: DragEvent) {
  return Array.from(event.dataTransfer?.types || []).includes('Files')
}

function handleDragEnter(event: DragEvent) {
  if (!hasFilePayload(event)) return
  dragDepth.value += 1
  isDragging.value = true
}

function handleDragOver(event: DragEvent) {
  if (!hasFilePayload(event)) return
  event.dataTransfer && (event.dataTransfer.dropEffect = 'copy')
}

function handleDragLeave(event: DragEvent) {
  if (!hasFilePayload(event)) return
  dragDepth.value = Math.max(0, dragDepth.value - 1)
  if (dragDepth.value === 0) {
    isDragging.value = false
  }
}

async function handleDrop(event: DragEvent) {
  if (!hasFilePayload(event)) return
  const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : []
  dragDepth.value = 0
  isDragging.value = false
  await addFiles(files)
}

function arrayBufferToBase64(buffer: ArrayBuffer) {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    bytes.forEach((b) => {
      binary += String.fromCharCode(b)
    })
    return window.btoa(binary)
  }
  const nodeBuffer = (globalThis as any).Buffer
  if (nodeBuffer) {
    return nodeBuffer.from(buffer).toString('base64')
  }
  throw new Error('Base64 encoding is not supported in this environment.')
}

function runUIDirectives(directives?: AdminIntelUIDirective[] | null) {
  if (!directives || !directives.length) return
  directives.forEach((directive) => {
    if (!directive) return
    if (directive.type === 'navigate') {
      const path = directive.data?.path || '/'
      const query = directive.data?.query || {}
      router.push({ path, query }).catch((err) => console.warn('Navigation directive failed', err))
    } else if (directive.type === 'toast' && directive.data?.message) {
      toast.add({
        severity: directive.data?.severity || 'info',
        summary: directive.data?.summary || 'إشعار',
        detail: directive.data.message,
        life: directive.data?.life || 3000,
      })
    }
  })
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} بايت`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} كيلوبايت`
  return `${(bytes / (1024 * 1024)).toFixed(1)} ميجابايت`
}

</script>

<style scoped>
.intel-chat-container {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

.intel-fab {
  width: 52px;
  height: 52px;
  border-radius: 999px;
  border: none;
  background: var(--p-primary-color, #0ea5e9);
  color: white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  font-size: 1.2rem;
}

.intel-panel {
  width: min(380px, 90vw);
  height: 520px;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 1rem;
  color: var(--p-text-color, #111827);
  position: relative;
}

.intel-header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #eee;
}

.intel-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.intel-header h4 {
  margin: 0;
}

.subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #6c7280;
}

.intel-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.intel-messages {
  background: #f9fafb;
  color: inherit;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #eceef5;
}

.message {
  margin-bottom: 0.9rem;
  padding: 0.65rem 0.9rem;
  border-radius: 0.75rem;
  line-height: 1.4;
  color: inherit;
}

.message.bot {
  background: #eef2ff;
  border: 1px solid #e0e7ff;
}

.message.user {
  background: #dcfce7;
  border: 1px solid #bbf7d0;
}

.intel-input {
  padding: 0.9rem 1.2rem 1.2rem;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  color: inherit;
}

.intel-upload-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.intel-file-input {
  display: none;
}

.attachment-count {
  color: #64748b;
}

.attachment-preview {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.attachment-preview li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.35rem 0.6rem;
  font-size: 0.85rem;
}

.attachment-preview button {
  border: none;
  background: transparent;
  color: #475569;
  cursor: pointer;
}

.message p {
  margin: 0;
  color: inherit;
}

.intel-panel :deep(p),
.intel-panel :deep(span),
.intel-panel :deep(li),
.intel-panel :deep(label),
.intel-panel :deep(input),
.intel-panel :deep(textarea) {
  color: inherit !important;
}

.intel-input :deep(.p-inputtextarea) {
  background: #fff;
  color: inherit;
}

.intel-panel :deep(.intel-table) {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.75rem;
  font-size: 0.9rem;
}

.intel-panel :deep(.intel-table th),
.intel-panel :deep(.intel-table td) {
  border: 1px solid #e5e7eb;
  padding: 6px 8px;
  text-align: right;
}

.intel-panel :deep(.intel-table thead) {
  background: #f4f5f7;
  font-weight: 600;
}

.intel-panel :deep(.intel-employee-link) {
  color: #0ea5e9;
  font-weight: 600;
  text-decoration: underline;
}

.intel-plan {
  padding-bottom: 0.5rem;
  border-top: 1px dashed #ccc;
  color: inherit;
}

.intel-choices {
  padding-bottom: 0.5rem;
  border-top: 1px dashed #ccc;
}

.intel-choices h5 {
  margin-bottom: 0.5rem;
}

.choice-block {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: #f8fafc;
}

.choice-label {
  margin: 0 0 0.5rem;
  font-weight: 600;
}

.choice-options {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.choice-option {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  cursor: pointer;
}

.choice-option-body {
  display: flex;
  flex-direction: column;
}

.choice-link {
  font-weight: 600;
  color: var(--p-primary-color, #0ea5e9);
  text-decoration: none;
}

.choice-link:hover {
  text-decoration: underline;
}

.choice-option small {
  color: #475569;
  display: block;
}

.choice-hint {
  display: block;
  margin-top: 0.25rem;
  color: #64748b;
}

.plan-summary {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem 0;
}

.plan-summary i {
  font-size: 1.2rem;
  color: var(--p-primary-color, #0ea5e9);
}

.plan-action {
  display: flex;
  gap: 0.65rem;
  padding: 0.6rem 0;
  border-bottom: 1px dashed #e5e7eb;
}

.plan-action:last-child {
  border-bottom: none;
}

.action-details {
  flex: 1;
}

.action-headline {
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  align-items: baseline;
}

.action-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.action-chip {
  background: #eef2ff;
  color: #3730a3;
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  font-size: 0.75rem;
}

.action-preview {
  margin: 0.35rem 0 0;
  font-size: 0.9rem;
  color: #334155;
}

.auto-pill {
  display: inline-block;
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: #0f766e;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.intel-drop-overlay {
  position: absolute;
  inset: 0;
  background: rgba(14, 165, 233, 0.08);
  border: 2px dashed var(--p-primary-color, #0ea5e9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  pointer-events: none;
  z-index: 10;
  text-align: center;
  color: var(--p-primary-color, #0ea5e9);
  font-weight: 600;
}

.intel-drop-overlay i {
  font-size: 2rem;
}
</style>
